/**
 * Certification Type enum
 * Defines the types of certifications that can be uploaded
 */
export type CertificationType =
  | 'faa-part-107'      // User - Remote Pilot Certificate
  | 'faa-part-108'      // User - Future replacement for Part 107
  | 'faa-part-137'      // Organization - Agricultural Aircraft Operations
  | 'faa-44807'         // Organization - Exemption for drone operations
  | 'state-applicator'  // User or Organization - State pesticide applicator license
  | 'medical'           // User or Organization - Medical certificate
  | 'insurance'         // Organization - Liability insurance
  | 'other';            // Other certifications

/**
 * Certification ownership type
 */
export type CertificationOwnerType = 'organization' | 'profile';

/**
 * Certification status
 */
export type CertificationStatus = 'active' | 'expired' | 'expiring-soon' | 'archived';

/**
 * Certification model
 * Represents a certification that belongs to either an organization or a user profile
 */
export interface Certification {
  id: string;
  certificationType: CertificationType;
  ownerType: CertificationOwnerType;
  organizationId?: string;  // Set when ownerType is 'organization'
  profileId?: string;       // Set when ownerType is 'profile'

  // Certificate details
  certificateNumber: string;
  issuedDate: string;
  expirationDate?: string;
  issuingAuthority: string;

  // State-specific (for state licenses)
  state?: string;

  // Document
  documentUrl?: string;
  documentFileName?: string;

  // Metadata
  notes?: string;
  status: CertificationStatus;

  // Audit fields
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy?: string;
}

/**
 * Certification create/update payload
 */
export interface CertificationCreate {
  certificationType: CertificationType;
  ownerType: CertificationOwnerType;
  organizationId?: string;
  profileId?: string;
  certificateNumber: string;
  issuedDate: string;
  expirationDate?: string;
  issuingAuthority: string;
  state?: string;
  documentUrl?: string;
  documentFileName?: string;
  notes?: string;
}

/**
 * Certification type metadata for UI display
 */
export interface CertificationTypeInfo {
  value: CertificationType;
  label: string;
  description: string;
  ownerType: CertificationOwnerType;
  isRequired: boolean;
}

/**
 * Organization certification types
 */
export const ORGANIZATION_CERTIFICATION_TYPES: CertificationTypeInfo[] = [
  {
    value: 'faa-44807',
    label: 'FAA 44807 Exemption',
    description: 'Section 44807 exemption for commercial drone operations',
    ownerType: 'organization',
    isRequired: true
  },
  {
    value: 'faa-part-137',
    label: 'FAA Part 137',
    description: 'Agricultural aircraft operations certificate',
    ownerType: 'organization',
    isRequired: true
  },
  {
    value: 'insurance',
    label: 'Liability Insurance',
    description: 'Commercial liability insurance for drone operations',
    ownerType: 'organization',
    isRequired: true
  },
  {
    value: 'medical',
    label: 'Medical Certificate',
    description: 'FAA medical certificate',
    ownerType: 'organization',
    isRequired: false
  },
  {
    value: 'state-applicator',
    label: 'State Applicator License',
    description: 'State-issued pesticide applicator license',
    ownerType: 'organization',
    isRequired: true
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Additional certification, license, or credential',
    ownerType: 'organization',
    isRequired: false
  },
];

/**
 * User (Profile) certification types
 */
export const USER_CERTIFICATION_TYPES: CertificationTypeInfo[] = [
  {
    value: 'faa-part-107',
    label: 'FAA Part 107',
    description: 'Remote Pilot Certificate for small UAS',
    ownerType: 'profile',
    isRequired: true
  },
  {
    value: 'faa-part-108',
    label: 'FAA Part 108',
    description: 'Future remote pilot certification (coming soon)',
    ownerType: 'profile',
    isRequired: false
  },
  {
    value: 'state-applicator',
    label: 'State Applicator License',
    description: 'State-issued pesticide applicator license',
    ownerType: 'profile',
    isRequired: true
  },
  {
    value: 'medical',
    label: 'Medical Certificate',
    description: 'FAA medical certificate',
    ownerType: 'profile',
    isRequired: false
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Additional certification, license, or credential',
    ownerType: 'profile',
    isRequired: false
  },
];

export function getCertificationStatus(
  status: CertificationStatus | null | undefined, 
  expirationDate: string | null | undefined
): string {
  if (!status) { 
    return 'Unknown';
  }

  if (status === 'archived') {
      return status;
  }

  if (expirationDate) {
    const expDate = new Date(expirationDate);
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    if (expDate < now) {
      return 'expired';
    }
    else if (expDate < thirtyDaysFromNow) {
      return 'expiring-soon';
    }
    
    return 'active';
  }

  return status;
}
